<script setup lang="ts">
import {fromLonLat, toLonLat} from "ol/proj";
import {COURSE_ADD_MODES, boatLabel, getSpeedColors, getVmcColors, type BoatLabelMode} from "~/utils";
import {LineString, Point} from "ol/geom";
import GeoJSON from "ol/format/GeoJSON";
import { Collection, Feature } from "ol";
import type { Course, Maneuver } from "~/stores";
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import { Modify, Translate, Draw } from 'ol/interaction'
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style'
import OlText from 'ol/style/Text'

import OlMap from 'vue3-openlayers/map/OlMap'
import OlView from 'vue3-openlayers/map/OlView'
import OlOverlay from 'vue3-openlayers/map/OlOverlay'
import OlFeature from 'vue3-openlayers/map/OlFeature'
import OlRotateControl from 'vue3-openlayers/controls/OlRotateControl'
import OlTileLayer from 'vue3-openlayers/layers/OlTileLayer'
import OlVectorLayer from 'vue3-openlayers/layers/OlVectorLayer'
import OlSourceXyz from 'vue3-openlayers/sources/OlSourceXYZ'
import OlSourceVector from 'vue3-openlayers/sources/OlSourceVector'
import OlGeomLineString from 'vue3-openlayers/geometries/OlGeomLineString'
import OlGeomPoint from 'vue3-openlayers/geometries/OlGeomPoint'
import OlGeomPolygon from 'vue3-openlayers/geometries/OlGeomPolygon'
import OlStyle from 'vue3-openlayers/styles/OlStyle'
import OlStyleStroke from 'vue3-openlayers/styles/OlStyleStroke'
import OlStyleFill from 'vue3-openlayers/styles/OlStyleFill'
import OlStyleCircle from 'vue3-openlayers/styles/OlStyleCircle'
import OlStyleIcon from 'vue3-openlayers/styles/OlStyleIcon'
import OlStyleText from 'vue3-openlayers/styles/OlStyleText'
import OlInteractionDraw from 'vue3-openlayers/interactions/OlInteractionDraw'

const {
  activities = [],
  geoJsonKey = 'geojson',
  minHeight = 400,
  fillParent = false,
  leagueRaceResult = null,
  highlightedFeatures = [],
  boatPositions = [],
  boatTails = [],
  colorSource = 'default',
  speedRange = null,
  vmcReferenceHeading = null,
  course = null,
  tacks = [],
  maneuvers = [],
  drawingType = null,
  drawMaxPoints = undefined,
  drawnFeatures = [],
  allowDrawing = false,
  playbackIndex = null,
  playbackIndices = [],
  windDirection = null,
  maneuversEditable = false,
  courseEditable = false,
  showViewControls = true,
  showZoomButtons = true,
  boatLabelMode = 'auto',
} = defineProps<{
  activities: Array<Activity>,
  geoJsonKey?: string,
  minHeight?: number,
  fillParent?: boolean,
  leagueRaceResult?: LeagueRaceResult,
  highlightedFeatures?: Array<GeoJSONFeature<{}, GeoJsonLineString>>,
  boatPositions?: Array<GeoJsonPoint>,
  boatTails?: Array<{ type: string; geometry: { type: string; coordinates: number[][] }; properties: { color: string } }>,
  colorSource: string,
  // Shared min/max (m/s) for speed colouring, so several tracks grade against one
  // scale instead of each against its own range. Null = per-track range.
  speedRange?: { min: number, max: number } | null,
  vmcReferenceHeading: null | number,
  course: null | Course,
  tacks?: Array<{ lng: number, lat: number, height_change_m: number | null }>,
  maneuvers?: Maneuver[],
  drawingType?: 'LineString' | 'Point' | null,
  drawMaxPoints?: number,
  drawnFeatures?: any[],
  allowDrawing?: boolean,
  playbackIndex?: number | null,
  playbackIndices?: number[],
  windDirection?: number | null,
  // Inline editor: the "+" button adds maneuvers (single activity, sails/[id]) and/or course
  // elements (sails/[id] and the multi-track analysis page); placed ones drag/click-to-delete.
  // Independent of allowDrawing/drawingType.
  maneuversEditable?: boolean,
  courseEditable?: boolean,
  // Hide the "reset zoom" and "follow boat" buttons (e.g. multi-track analysis, where
  // panning/zooming across several tracks makes auto-follow/refit behavior unwanted).
  showViewControls?: boolean,
  // Hide the zoom-in / zoom-out (+ / −) buttons, e.g. small non-interactive preview
  // maps in activity tiles/cards.
  showZoomButtons?: boolean,
  // Label priority for boats built from `activities` here (hover preview, follow
  // list) — should match whatever built `boatPositions` labels. See boatLabel.
  boatLabelMode?: BoatLabelMode,
}>()

const emit = defineEmits<{
  'drawend': [feature: any]
  'update:drawnFeatures': [features: any[]]
  'add-maneuver': [payload: { maneuver_type: string, latitude: number, longitude: number, timestamp: string }]
  'delete-maneuver': [id: number]
  'reposition-maneuver': [payload: { id: number, latitude: number, longitude: number, timestamp?: string }]
}>()

// Scratch collection for the draw interaction — cleared after each drawend
// so completed features don't duplicate with the prop-driven display layer.
const drawCollection = new Collection<any>([])
const geoJsonFormat = new GeoJSON()

// ── Editable drawn-features layer ────────────────────────────────────────────
// One OL VectorSource that is kept in sync with the drawnFeatures prop.
// Modify (vertex drag) and Translate (whole-feature drag) interactions operate
// on this source and emit update:drawnFeatures when they finish.

const editableSource = new VectorSource()

const buildEditStyle = (feature: any): Style => {
  const geomType = feature.getGeometry()?.getType()
  const props = feature.getProperties()
  if (geomType === 'LineString') {
    const color = props.gateType === 'start' ? '#4caf50'
      : props.gateType === 'start_finish' ? '#ff9800'
      : props.gateType === 'finish' ? '#f44336' : '#2196f3'
    const label = props.gateType === 'start' ? 'Start'
      : props.gateType === 'start_finish' ? 'Start/Finish'
      : props.gateType === 'finish' ? 'Finish' : 'Gate'
    return new Style({
      stroke: new Stroke({ color, width: 3 }),
      text: new OlText({ text: label, fill: new Fill({ color }), offsetY: -10 }),
    })
  }
  const color = props.markType === 'upwind' ? '#ff5722' : props.markType === 'downwind' ? '#9c27b0' : '#607d8b'
  // Number marks of the same type (U1, U2, ... / D1, D2, ... / M1, M2, ...) so repeated laps
  // with several marks of the same kind stay distinguishable on the map.
  const sameTypeMarks = editableSource.getFeatures()
    .filter(f => f.getGeometry()?.getType() === 'Point' && f.getProperties().markType === props.markType)
    .sort((a, b) => (a.getId() as number) - (b.getId() as number))
  const index = sameTypeMarks.indexOf(feature)
  const suffix = sameTypeMarks.length > 1 && index !== -1 ? String(index + 1) : ''
  const label = (props.markType === 'upwind' ? 'U' : props.markType === 'downwind' ? 'D' : 'M') + suffix
  return new Style({
    image: new CircleStyle({
      radius: 8,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: 'white', width: 2 }),
    }),
    text: new OlText({ text: label, fill: new Fill({ color: 'white' }), offsetY: 1 }),
  })
}

const editableLayer = new VectorLayer({
  source: editableSource,
  style: buildEditStyle,
  zIndex: 10,
})

// Keep editableSource in sync whenever drawnFeatures changes from the parent.
watch(
  () => drawnFeatures,
  (features) => {
    editableSource.clear()
    features.forEach((f, i) => {
      const olFeature = geoJsonFormat.readFeature(f, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:4326',
      })
      olFeature.setId(i)
      editableSource.addFeature(olFeature)
    })
  },
  { deep: true, immediate: true }
)

// Read features back from the source (in original order) and emit to parent.
const emitUpdatedFeatures = () => {
  const features = [...editableSource.getFeatures()]
  features.sort((a, b) => (a.getId() as number) - (b.getId() as number))
  const updated = features.map(f =>
    geoJsonFormat.writeFeatureObject(f, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    })
  )
  emit('update:drawnFeatures', updated)
}

const onDrawEnd = (event: any) => {
  const feature = geoJsonFormat.writeFeatureObject(event.feature, {
    featureProjection: 'EPSG:3857',
    dataProjection: 'EPSG:4326',
  })
  nextTick(() => drawCollection.clear())
  emit('drawend', feature)
}

// Gate colour and label read from explicit gateType property set by the parent
const gateLineColor = (feature: any) => {
  if (feature.properties?.gateType === 'start') return '#4caf50'
  if (feature.properties?.gateType === 'start_finish') return '#ff9800'
  if (feature.properties?.gateType === 'finish') return '#f44336'
  return '#2196f3'
}

const gateLabel = (feature: any) => {
  if (feature.properties?.gateType === 'start') return 'Start'
  if (feature.properties?.gateType === 'start_finish') return 'Start/Finish'
  if (feature.properties?.gateType === 'finish') return 'Finish'
  return 'Gate'
}

const markFillColor = (feature: any) =>
  feature.properties?.markType === 'upwind' ? '#ff5722' : '#9c27b0'

const markLabel = (feature: any) =>
  feature.properties?.markType === 'upwind' ? 'U' : 'D'
const zoom = ref(5)
const center = reactive ([0, 0])
const mHeight = ref(minHeight)
const store = useMainStore()
const colors = store.colors

// activities
// Activities without a track (e.g. simplified_geojson still null) are skipped;
// activityIndex keeps the original index for colours and playbackIndices.
const geoJsonFeatures = computed(() => activities.flatMap((activity, i) => activity[geoJsonKey]?.geometry?.coordinates?.length ? [{
  type: 'Feature',
  id: `track-${activity.id}`,
  activityIndex: i,
  geometry: {
    ...activity[geoJsonKey].geometry,
    coordinates: activity[geoJsonKey].geometry.coordinates.map((c, i) =>  [...fromLonLat(c), i])
  },
  properties: {
    // Lets a pointer-move hit-test map a hovered track line back to its boat
    // (boatKey() === activity.id) so the boat info card can pop on track hover.
    activityId: activity.id,
    defaultColor: colors[i % colors.length],
    colorForSpeed: getSpeedColors(activity?.speed_ms, speedRange?.min, speedRange?.max),
    colorForVmc: vmcReferenceHeading !== null ? getVmcColors(activity, vmcReferenceHeading) : null,
    colorForLiftedTack: vmcReferenceHeading !== null ? getLiftedTackColors(activity, vmcReferenceHeading, colors[i % colors.length]) : null
  }
}] : []))

// Precomputed, per-activity arrays of small solid-colour ol/style/Style
// objects (one LineString per run of consecutive same-coloured points),
// applied to the track feature via OlStyle's overrideStyleFunction.
// Unlike FlowLine (which re-samples ~255 colour chunks from the geometry's
// current on-screen pixel length on every render) these are computed once
// from the activity data and never resampled, so colours stay pinned to
// their real GPS points regardless of zoom, pan, or replay progress.
const trackSegmentStyles = computed(() => geoJsonFeatures.value.map(f => {
  const coords = f.geometry.coordinates
  const defaultColor = f.properties.defaultColor
  const values = colorSource !== 'default' ? (f.properties as any)[colorSource] : null
  const colorAt = (idx: number) => (values && values.length ? (values[idx] ?? defaultColor) : defaultColor)

  const styles: Style[] = []
  let start = 0
  while (start < coords.length - 1) {
    const color = colorAt(start)
    let end = start + 1
    while (end < coords.length - 1 && colorAt(end) === color) end++
    styles.push(new Style({
      geometry: new LineString(coords.slice(start, end + 1)),
      stroke: new Stroke({ color, width: 2 })
    }))
    start = end
  }
  return styles
}))

const isPlaybackActive = computed(() => {
  if (playbackIndices.length > 0) return playbackIndices.some(idx => idx > 0)
  return playbackIndex != null && playbackIndex > 0
})

const getEffectiveIndex = (i: number): number => {
  if (playbackIndices.length > 0) return playbackIndices[i] ?? 0
  return playbackIndex ?? 0
}

// The not-yet-sailed portion of each track, dimmed on top of the (always
// full-opacity, always stable-colored) base line during replay. This is a
// plain solid-color stroke rather than a FlowLine, so unlike the old
// per-vertex-colored "sailed" overlay it isn't sampled at a fixed ~255 chunks
// across the line: its geometry can grow/shrink every replay tick with no
// resampling artifacts, and its boundary sits exactly at the current replay
// vertex instead of snapping to the nearest chunk boundary.
const unplayedGeoJsonFeatures = computed(() => {
  if (!isPlaybackActive.value) return []
  return geoJsonFeatures.value.map((f) => {
    const idx = getEffectiveIndex(f.activityIndex)
    const coords = f.geometry.coordinates
    if (idx >= coords.length - 1) return null
    return {
      ...f,
      geometry: {
        ...f.geometry,
        coordinates: coords.slice(Math.max(idx, 0))
      }
    }
  })
})

const mapRef = ref(null)
const viewRef = ref(null)

// ── Map rotation / wind-up mode ──────────────────────────────────────────────
// windDirection is the direction the wind comes FROM in compass degrees.
// Rotating the view by -windDirection puts that bearing at the top of the screen.
const currentRotation = ref(0)

const windUpRotation = computed(() =>
  windDirection != null ? -windDirection * Math.PI / 180 : null
)

const normalizeAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a))

const isWindUp = computed(() =>
  windUpRotation.value !== null &&
  Math.abs(normalizeAngle(currentRotation.value - windUpRotation.value)) < 0.01
)

const toggleWindUp = () => {
  const view = viewRef.value?.view
  if (!view || windUpRotation.value === null) return
  view.animate({ rotation: isWindUp.value ? 0 : windUpRotation.value, duration: 300 })
}

// If the wind direction changes while already in wind-up mode, re-align the
// view to the new bearing instead of leaving it pointed at the stale one.
watch(windUpRotation, (newRot, oldRot) => {
  const view = viewRef.value?.view
  if (!view || newRot == null || oldRot == null) return
  if (Math.abs(normalizeAngle(currentRotation.value - oldRot)) < 0.01) {
    view.animate({ rotation: newRot, duration: 300 })
  }
})

const MANEUVER_STYLE: Record<string, { color: string; prefix: string }> = {
  TA: { color: 'rgba(255,152,0,0.9)',  prefix: 'T'  },
  GY: { color: 'rgba(76,175,80,0.9)',  prefix: 'G'  },
  UM: { color: 'rgba(156,39,176,0.9)', prefix: 'UM' },
  DM: { color: 'rgba(156,39,176,0.9)', prefix: 'DM' },
  GL: { color: 'rgba(156,39,176,0.9)', prefix: 'GL' },
  GR: { color: 'rgba(156,39,176,0.9)', prefix: 'GR' },
  OM: { color: 'rgba(156,39,176,0.9)', prefix: 'OM' },
}
const DEFAULT_MANEUVER_STYLE = { color: 'rgba(96,125,139,0.9)', prefix: '?' }

const maneuverMarkers = computed(() => {
  const counters: Record<string, number> = {}
  return maneuvers
    .filter((m: Maneuver) => !m.is_deleted)
    .map((m: Maneuver) => {
      const style = MANEUVER_STYLE[m.maneuver_type] ?? DEFAULT_MANEUVER_STYLE
      counters[style.prefix] = (counters[style.prefix] ?? 0) + 1
      return { m, color: style.color, label: `${style.prefix}${counters[style.prefix]}` }
    })
})

// ── Maneuver edit layer (imperative OL source so it supports drag + click-pick) ──
// Rendered for everyone; drag/click-to-delete only wired up when maneuversEditable.
const maneuverEditSource = new VectorSource()
const maneuverEditLayer = new VectorLayer({
  source: maneuverEditSource,
  zIndex: 15,
  style: (feature: any) => new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: feature.get('color') }),
      stroke: new Stroke({ color: 'white', width: 1.5 }),
    }),
    text: new OlText({ text: feature.get('label'), offsetY: -14 }),
  }),
})

const syncManeuverEditLayer = () => {
  maneuverEditSource.clear()
  for (const { m, color, label } of maneuverMarkers.value) {
    const feature = new Feature({ geometry: new Point(fromLonLat([m.longitude, m.latitude])) })
    feature.set('maneuver_id', m.id)
    feature.set('color', color)
    feature.set('label', label)
    maneuverEditSource.addFeature(feature)
  }
}
watch(maneuverMarkers, syncManeuverEditLayer, { immediate: true, deep: true })

// ── Inline "+" editor: add maneuvers/course elements, place by clicking the map ──

const MANEUVER_TYPES = [
  { value: 'TA', title: 'Tack',                   color: '#FF6B35' },
  { value: 'GY', title: 'Gybe',                   color: '#2196F3' },
  { value: 'UM', title: 'Upwind mark rounding',   color: '#F44336' },
  { value: 'DM', title: 'Downwind mark rounding', color: '#9C27B0' },
  { value: 'GL', title: 'Gate rounding (left)',   color: '#9C27B0' },
  { value: 'GR', title: 'Gate rounding (right)',  color: '#9C27B0' },
  { value: 'OM', title: 'Offset mark rounding',   color: '#FF9800' },
  { value: 'PT', title: 'Penalty turn',           color: '#795548' },
  { value: 'BR', title: 'Broach',                 color: '#607D8B' },
  { value: 'CA', title: 'Capsize',                color: '#F44336' },
  { value: 'OT', title: 'Other',                  color: '#9E9E9E' },
]

type AddMode = 'maneuver' | 'gate_start' | 'gate_start_finish' | 'gate_finish' | 'gate' | 'mark' | null

const showAddDialog = ref(false)
const addMode = ref<AddMode>(null)
const pendingManeuverType = ref('TA')

const startAddManeuver = (type: string) => {
  pendingManeuverType.value = type
  addMode.value = 'maneuver'
  showAddDialog.value = false
}
const startAddCourse = (mode: AddMode) => {
  addMode.value = mode
  showAddDialog.value = false
}
const cancelAdd = () => { addMode.value = null }

const addModeLabel = computed(() => {
  if (addMode.value === 'maneuver') {
    const t = MANEUVER_TYPES.find(t => t.value === pendingManeuverType.value)
    return `Click the track to place: ${t?.title ?? 'maneuver'}`
  }
  const c = COURSE_ADD_MODES.find(c => c.mode === addMode.value)
  return c ? `Click the map to draw: ${c.label}` : ''
})

// Text on the "+" button, naming what it adds on this map.
const addButtonLabel = computed(() =>
  maneuversEditable && courseEditable ? 'Add course / maneuver'
    : maneuversEditable ? 'Add maneuver'
    : 'Add course element')

// Nearest track-point index to a clicked lon/lat — maneuvers snap to the GPS track.
const snapToTrack = (lonLat: number[]): number => {
  const coords = activities[0]?.geojson?.geometry?.coordinates ?? []
  let minDist = Infinity
  let nearestIdx = 0
  for (let i = 0; i < coords.length; i++) {
    const dx = coords[i][0] - lonLat[0]
    const dy = coords[i][1] - lonLat[1]
    const d = dx * dx + dy * dy
    if (d < minDist) { minDist = d; nearestIdx = i }
  }
  return nearestIdx
}

const insertCourseFeature = (features: any[], feature: any) => {
  const gateType = feature.properties?.gateType
  const next = [...features]
  if (gateType === 'start' || gateType === 'start_finish') next.unshift(feature)
  else if (gateType === 'finish') next.push(feature)
  else {
    const finishIdx = next.findIndex(f => f.properties?.gateType === 'finish')
    if (finishIdx === -1) next.push(feature)
    else next.splice(finishIdx, 0, feature)
  }
  return next
}

// ── Click-to-delete popup for existing maneuvers/course marks ────────────────
// Click/tap-triggered (not hover-based) so it works identically on desktop and
// mobile, and the Delete button stays put once shown.
type DeleteConfirm =
  | { kind: 'maneuver', id: number, label: string, typeDisplay: string, coord: number[], stats: ReturnType<typeof maneuverStats> }
  | { kind: 'course', index: number, label: string, coord: number[] }
const deleteConfirm = ref<DeleteConfirm | null>(null)

const confirmDelete = () => {
  if (!deleteConfirm.value) return
  if (deleteConfirm.value.kind === 'maneuver') {
    emit('delete-maneuver', deleteConfirm.value.id)
  } else {
    emit('update:drawnFeatures', drawnFeatures.filter((_, i) => i !== (deleteConfirm.value as any).index))
  }
  deleteConfirm.value = null
}

// ── Maneuver hover overlay (glance-only; stays pointer-events:none since a hover
// popup can't reliably be reached before the mouse leaves the hit zone — the
// interactive delete lives in the click/tap-triggered popup below instead) ──────
const { formatSpeed } = useSpeedUnit()
const hoveredManeuver = ref<{ m: Maneuver, label: string, coord: number[] } | null>(null)
const MANEUVER_HOVER_RADIUS_PX = 10

const maneuverStats = (m: Maneuver) => {
  const a = m.analysis
  return {
    entry: a?.entry_speed_ms ?? m.speed_before_ms,
    exit: a?.exit_speed_ms ?? m.speed_after_ms,
    tackAngle: a?.tack?.tack_angle ?? null,
    heightChange: a?.tack?.height_change_m ?? null,
  }
}

const onMapPointerMove = (map: any, evt: any) => {
  if (evt.dragging) {
    hoveredManeuver.value = null
    hoveredBoatKey.value = null
    hoveredTrack.value = null
    return
  }
  let best: { m: Maneuver, label: string, coord: number[] } | null = null
  let bestDist = MANEUVER_HOVER_RADIUS_PX
  for (const { m, label } of maneuverMarkers.value) {
    const coord = fromLonLat([m.longitude, m.latitude])
    const pixel = map.getPixelFromCoordinate(coord)
    if (!pixel) continue
    const d = Math.hypot(pixel[0] - evt.pixel[0], pixel[1] - evt.pixel[1])
    if (d <= bestDist) {
      bestDist = d
      best = { m, label, coord }
    }
  }
  hoveredManeuver.value = best

  // Hovering a track line previews that boat at the hovered point (icon + info
  // card jump there). Track hover wins over the boat-marker hint: the previewed
  // icon lands under the cursor, and letting the marker hint re-fire there would
  // just fight it. A maneuver under the cursor still takes priority over both.
  const track = best ? null : findTrackAtPixel(map, evt)
  hoveredTrack.value = track
  hoveredBoatKey.value = track ? null : findBoatKeyAtPixel(map, evt.pixel)

  const target = map.getTargetElement()
  if (target) target.style.cursor = (best || hoveredBoatKey.value != null) ? 'pointer' : ''
}

const hoveredManeuverDetails = computed(() =>
  hoveredManeuver.value ? maneuverStats(hoveredManeuver.value.m) : null
)

// ── Programmatic "show this maneuver on the map" (triggered from the maneuvers
// list) — pans/zooms to it and reuses the hover popup to display its stats. ────
let focusHideTimer: ReturnType<typeof setTimeout> | null = null
const focusManeuver = (id: number) => {
  const entry = maneuverMarkers.value.find(({ m }) => m.id === id)
  if (!entry) return
  const coord = fromLonLat([entry.m.longitude, entry.m.latitude])
  const view = viewRef.value?.view
  if (view) {
    hasUserInteracted = true
    view.animate({ center: coord, zoom: Math.max(view.getZoom() ?? 0, 17), duration: 500 })
  }
  hoveredManeuver.value = { m: entry.m, label: entry.label, coord }
  if (focusHideTimer) clearTimeout(focusHideTimer)
  focusHideTimer = setTimeout(() => { hoveredManeuver.value = null }, 4000)
}

// Draws attention to the "+" FAB (e.g. from the Course Analysis tab's "draw a course" empty-state
// hint, when there's no course yet to show/pan to).
const highlightAdd = ref(false)
let highlightAddTimer: ReturnType<typeof setTimeout> | null = null
const highlightAddButton = () => {
  highlightAdd.value = false
  nextTick(() => {
    highlightAdd.value = true
    if (highlightAddTimer) clearTimeout(highlightAddTimer)
    highlightAddTimer = setTimeout(() => { highlightAdd.value = false }, 2500)
  })
}

// Lets a parent (e.g. the multi-track analysis page's "Edit course" dialog, or the
// maneuvers list) trigger click-to-place mode or pan/zoom to a specific maneuver.
defineExpose({ startAddCourse, focusManeuver, highlightAddButton })

// ── Boat info dialog (time/speed/COG) ─────────────────────────────────────────
// Hidden by default; hover the boat for a "Show boat info" hint, click to open.
// Keyed by properties.id when available (stable across activity add/remove), else index.
const BOAT_HOVER_RADIUS_PX = 16
const expandedBoatKeys = ref<Set<string | number>>(new Set())
const hoveredBoatKey = ref<string | number | null>(null)
// Track line currently under the cursor: its boat key (=== activity id) plus the
// nearest GPS-point index, so the boat icon + info card can preview that spot.
const hoveredTrack = ref<{ key: string | number, idx: number } | null>(null)
const boatKey = (boat: any, i: number) => boat.properties?.id ?? boat.properties?.entry_id ?? i
const collapseBoat = (key: string | number) => {
  expandedBoatKeys.value.delete(key)
  expandedBoatKeys.value = new Set(expandedBoatKeys.value)
  // Drop any manual drag offset so reopening the card starts back at the boat.
  if (key in boatCardOffsets.value) {
    const next = { ...boatCardOffsets.value }
    delete next[key]
    boatCardOffsets.value = next
  }
}
const expandBoat = (key: string | number) => { expandedBoatKeys.value.add(key); expandedBoatKeys.value = new Set(expandedBoatKeys.value) }

// Per-card manual drag offset (px, relative to the card's default anchored spot next
// to the boat). Lets the user pull overlapping info cards apart when boats are sailing
// close together. Keyed by the same boatKey() as expandedBoatKeys.
const BOAT_CARD_BASE_OFFSET: [number, number] = [16, 0]
const boatCardOffsets = ref<Record<string | number, [number, number]>>({})
const boatCardOffset = (key: string | number): [number, number] => {
  const [dx, dy] = boatCardOffsets.value[key] ?? [0, 0]
  return [BOAT_CARD_BASE_OFFSET[0] + dx, BOAT_CARD_BASE_OFFSET[1] + dy]
}

let boatCardDrag: { key: string | number; startX: number; startY: number; baseX: number; baseY: number } | null = null

const onBoatCardDragMove = (ev: PointerEvent) => {
  if (!boatCardDrag) return
  boatCardOffsets.value = {
    ...boatCardOffsets.value,
    [boatCardDrag.key]: [
      boatCardDrag.baseX + (ev.clientX - boatCardDrag.startX),
      boatCardDrag.baseY + (ev.clientY - boatCardDrag.startY),
    ],
  }
}
const onBoatCardDragEnd = () => {
  boatCardDrag = null
  window.removeEventListener('pointermove', onBoatCardDragMove)
  window.removeEventListener('pointerup', onBoatCardDragEnd)
}
const startBoatCardDrag = (key: string | number, ev: PointerEvent) => {
  // Ignore drags that start on the close button.
  if ((ev.target as HTMLElement).closest('.boat-metrics-close')) return
  ev.preventDefault()
  const [baseX, baseY] = boatCardOffsets.value[key] ?? [0, 0]
  boatCardDrag = { key, startX: ev.clientX, startY: ev.clientY, baseX, baseY }
  window.addEventListener('pointermove', onBoatCardDragMove)
  window.addEventListener('pointerup', onBoatCardDragEnd)
}

onUnmounted(onBoatCardDragEnd)
const toggleBoatExpanded = (key: string | number) => {
  if (expandedBoatKeys.value.has(key)) collapseBoat(key)
  else expandBoat(key)
}

// While a track line is hovered, the icon + info card for that boat jump to the
// hovered GPS point (with its time/speed/heading), leaving every other boat at
// the live playback position. Everything downstream (markers, cards, marker
// hit-testing) reads this instead of the raw boatPositions prop.
//
// A boat that hasn't started yet on the shared timeline is absent from
// boatPositions (see MultiPlayer), so for it we append a synthetic preview
// entry rather than overriding an existing one.
const displayBoatPositions = computed(() => {
  const h = hoveredTrack.value
  if (!h) return boatPositions

  const aIdx = activities.findIndex(a => a.id === h.key)
  const activity = aIdx >= 0 ? activities[aIdx] : null
  const coords = activity?.geojson?.geometry?.coordinates ?? []
  if (!activity || !coords.length) return boatPositions

  const idx = Math.min(Math.max(h.idx, 0), coords.length - 1)
  const headingDeg = activity.heading_deg?.[idx]
  const previewGeometry = { type: 'Point', coordinates: coords[idx] }
  const previewProps = {
    id: activity.id,
    color: colors[aIdx % colors.length],
    heading_rad: headingDeg != null ? headingDeg * Math.PI / 180 : 0,
    label: boatLabel(activity, boatLabelMode),
    skipper: activity.user_details?.name ?? '',
    metrics: {
      time: activity.time?.[idx] != null ? timeFromTimestamp(activity.time[idx]) : null,
      speed: activity.speed_ms?.[idx] != null ? formatSpeed(activity.speed_ms[idx]) : null,
      heading: headingDeg != null ? `${round(headingDeg)}°` : null,
    },
  }

  let matched = false
  const next = boatPositions.map((boat, i) => {
    if (boatKey(boat as any, i) !== h.key) return boat
    matched = true
    return { ...(boat as any), geometry: previewGeometry, properties: { ...(boat as any).properties, ...previewProps } }
  })
  if (!matched) next.push({ type: 'Feature', geometry: previewGeometry, properties: previewProps } as any)
  return next
})

const boatsWithMetrics = computed(() =>
  displayBoatPositions.value
    .map((boat, i) => ({ boat: boat as any, key: boatKey(boat, i) }))
    .filter(({ boat }) => boat.properties?.metrics)
)

const hasBoatMetrics = computed(() => boatsWithMetrics.value.length > 0)
const allBoatsExpanded = computed(() =>
  hasBoatMetrics.value && boatsWithMetrics.value.every(({ key }) => expandedBoatKeys.value.has(key))
)
const toggleAllBoats = () => {
  expandedBoatKeys.value = allBoatsExpanded.value
    ? new Set()
    : new Set(boatsWithMetrics.value.map(({ key }) => key))
}

const findBoatKeyAtPixel = (map: any, pixel: number[]): string | number | null => {
  let best: string | number | null = null
  let bestDist = BOAT_HOVER_RADIUS_PX
  for (const { boat, key } of boatsWithMetrics.value) {
    const coord = fromLonLat(boat.geometry.coordinates)
    const boatPixel = map.getPixelFromCoordinate(coord)
    if (!boatPixel) continue
    const d = Math.hypot(boatPixel[0] - pixel[0], boatPixel[1] - pixel[1])
    if (d <= bestDist) {
      bestDist = d
      best = key
    }
  }
  return best
}

// Nearest track vertex to the cursor, if one is within tolerance — returns that
// track's boat key (=== activity id) and the vertex index (which is also the
// index into time/speed/heading_deg). Works straight off the pre-projected,
// cached geoJsonFeatures coords, so it doesn't depend on OL feature-level hit
// detection (which proved unreliable for these thin multi-layer polylines).
const TRACK_HOVER_TOLERANCE_PX = 8
const findTrackAtPixel = (map: any, evt: any): { key: string | number, idx: number } | null => {
  const cursor = evt.coordinate
  if (!cursor) return null
  const resolution = map.getView().getResolution() ?? 1
  const tolSq = (TRACK_HOVER_TOLERANCE_PX * resolution) ** 2
  let best: { key: string | number, idx: number } | null = null
  let bestSq = tolSq
  for (const f of geoJsonFeatures.value) {
    const key = (f.properties as any).activityId
    const coords = f.geometry.coordinates
    for (let i = 0; i < coords.length; i++) {
      const dx = coords[i][0] - cursor[0]
      const dy = coords[i][1] - cursor[1]
      const dSq = dx * dx + dy * dy
      if (dSq < bestSq) {
        bestSq = dSq
        best = { key, idx: i }
      }
    }
  }
  return best
}

const hoveredBoatHint = computed(() => {
  if (hoveredBoatKey.value == null) return null
  const entry = boatsWithMetrics.value.find(b => b.key === hoveredBoatKey.value)
  if (!entry) return null
  return {
    coord: fromLonLat(entry.boat.geometry.coordinates),
    text: expandedBoatKeys.value.has(hoveredBoatKey.value) ? 'Hide boat info' : 'Show boat info',
  }
})

// ── Follow boat(s) ────────────────────────────────────────────────────────────
// User picks one or more boats from a menu; while any are selected, the view
// keeps re-centering (single boat) or re-fitting (multiple boats) on their
// current playback position. Cancelled by any manual pan/zoom (see pointerdown).
// boatPositions only lists boats that have actually started on the shared
// playback timeline (see MultiPlayer's targetTime filtering), so a boat that
// starts later is briefly missing from it — union in a placeholder sourced
// from `activities` (always the full, stable set) so every added track is
// selectable up front; it's replaced by the richer live entry once available.
const followableBoats = computed(() => {
  const fromPositions = boatPositions.map((boat, i) => ({
    key: boatKey(boat, i),
    color: (boat as any).properties?.color ?? colors[i % colors.length],
    label: (boat as any).properties?.label || `Boat ${i + 1}`,
  }))
  const seen = new Set(fromPositions.map(b => b.key))
  const fromActivitiesOnly = activities
    .map((a, i) => ({ key: a.id, color: colors[i % colors.length], label: boatLabel(a, boatLabelMode) || `Boat ${i + 1}` }))
    .filter(b => !seen.has(b.key))
  return [...fromPositions, ...fromActivitiesOnly]
})
const followedBoatKeys = ref<Set<string | number>>(new Set())
const isFollowing = computed(() => followedBoatKeys.value.size > 0)
const toggleFollowBoat = (key: string | number) => {
  const next = new Set(followedBoatKeys.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  followedBoatKeys.value = next
}

let trackExtent: number[] | null = null
let hasUserInteracted = false
let performFit: (() => void) | null = null

const resetZoom = () => {
  followedBoatKeys.value = new Set()
  hasUserInteracted = false
  performFit?.()
}

// Custom replacements for OL's hidden default zoom control (see .ol-zoom in the
// style block) so the +/- buttons match the other map buttons.
const zoomByDelta = (delta: number) => {
  const view = viewRef.value?.view
  const z = view?.getZoom()
  if (z == null) return
  view.animate({ zoom: z + delta, duration: 200 })
}

// Keep mHeight in sync for the explicit-height (non-fillParent) case.
// OL's own ResizeObserver detects the CSS height change and fires change:size → re-fit.
watch(() => minHeight, (val) => { mHeight.value = val })

onMounted(async () => {
  await nextTick()
  let latitudes = []
  let longitudes = []
  const geometries = [
    ...geoJsonFeatures.value.map(f => f.geometry)
  ]
  if (geometries.length) {
    latitudes = latitudes.concat(
        ...geometries.map(g => g.type === 'Point' ? [g.coordinates[1]] : g.coordinates.map(c => c[1]))
    )
    longitudes = longitudes.concat(
        ...geometries.map(g => g.type === 'Point' ? [g.coordinates[0]] : g.coordinates.map(c => c[0]))
    )
  }

  const minCoordinate = [Math.min(...longitudes), Math.min(...latitudes)]
  const maxCoordinate = [Math.max(...longitudes), Math.max(...latitudes)]
  trackExtent = [...minCoordinate, ...maxCoordinate]

  try {
    const map = mapRef.value?.map
    const view = viewRef.value?.view

    performFit = () => {
      if (hasUserInteracted || !trackExtent) return
      const size = map.getSize()
      if (size?.[0] && size?.[1]) {
        view.fit(trackExtent, { size, maxZoom: 15, padding: [10, 10, 10, 10] })
      }
    }

    // OL fires change:size via its ResizeObserver whenever the canvas size changes.
    // This covers both initial render and layout changes (e.g. grid restored from localStorage).
    map.on('change:size', performFit)
    performFit()

    // Stop auto-fitting/following once the user pans or zooms.
    map.on('pointerdown', () => {
      hasUserInteracted = true
      followedBoatKeys.value = new Set()
    })

    view.on('change:rotation', () => { currentRotation.value = view.getRotation() })

    // Follow boat(s): re-centre (single) or re-fit (multiple) on every position
    // update while at least one boat is selected. boatPositions is already
    // throttled to at most once per animation frame by the parent player.
    watch(
      () => boatPositions,
      (positions) => {
        if (!isFollowing.value) return
        const followed = positions
          .map((boat, i) => ({ boat: boat as any, key: boatKey(boat, i) }))
          .filter(({ key }) => followedBoatKeys.value.has(key))
        if (!followed.length) return
        const coords = followed.map(({ boat }) => fromLonLat(boat.geometry.coordinates))
        const size = map.getSize()
        if (!size?.[0] || !size?.[1]) return
        if (coords.length === 1) {
          view.animate({ center: coords[0], duration: 0 })
        } else {
          const lons = coords.map(c => c[0])
          const lats = coords.map(c => c[1])
          const extent = [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)]
          view.fit(extent, { size, maxZoom: 17, padding: [60, 60, 60, 60], duration: 0 })
        }
      },
      { deep: true }
    )

    // Maneuver hover overlay
    map.on('pointermove', (evt: any) => onMapPointerMove(map, evt))
    map.getViewport().addEventListener('mouseleave', () => {
      hoveredManeuver.value = null
      hoveredBoatKey.value = null
      hoveredTrack.value = null
    })

    // Click: maneuver/course-mark delete-confirm popup takes priority (when editable),
    // then boat info toggle. Suppressed entirely while placing a new item via "+".
    map.on('click', (evt: any) => {
      if (addMode.value) return

      if (maneuversEditable) {
        const hit = map.forEachFeatureAtPixel(
          evt.pixel, (f: any) => f,
          { layerFilter: (l: any) => l === maneuverEditLayer, hitTolerance: 10 }
        )
        if (hit) {
          const maneuverId = hit.get('maneuver_id')
          const entry = maneuverMarkers.value.find(x => x.m.id === maneuverId)
          if (entry) {
            hoveredManeuver.value = null
            deleteConfirm.value = {
              kind: 'maneuver', id: maneuverId, label: entry.label,
              typeDisplay: entry.m.maneuver_type_display,
              coord: fromLonLat([entry.m.longitude, entry.m.latitude]),
              stats: maneuverStats(entry.m),
            }
            return
          }
        }
      }

      if (courseEditable) {
        const hit = map.forEachFeatureAtPixel(
          evt.pixel, (f: any) => f,
          { layerFilter: (l: any) => l === editableLayer, hitTolerance: 10 }
        )
        if (hit) {
          const index = hit.getId() as number
          const f = drawnFeatures[index]
          if (f) {
            const coord = f.geometry.type === 'Point'
              ? fromLonLat(f.geometry.coordinates)
              : fromLonLat(f.geometry.coordinates[0])
            const label = f.geometry.type === 'LineString'
              ? (f.properties?.gateType ?? 'gate')
              : (f.properties?.markType ?? 'mark')
            deleteConfirm.value = { kind: 'course', index, label, coord }
            return
          }
        }
      }

      const key = findBoatKeyAtPixel(map, evt.pixel)
      if (key != null) toggleBoatExpanded(key)
    })

    // Add the programmatic editable layer and, when drawing is allowed,
    // Modify (vertex drag) + Translate (whole-feature drag) interactions.
    map.addLayer(editableLayer)

    if (allowDrawing || courseEditable) {
      // Gates/marks are always straight 2-point lines or single points — never
      // let Modify insert a new mid-segment vertex, which would "bend" a gate.
      const modify = new Modify({ source: editableSource, insertVertexCondition: () => false })
      const translate = new Translate({ layers: [editableLayer] })
      modify.on('modifyend', emitUpdatedFeatures)
      translate.on('translateend', emitUpdatedFeatures)
      map.addInteraction(modify)
      map.addInteraction(translate)
    }

    // Maneuver marker layer: always shown, drag-to-reposition only when editable
    map.addLayer(maneuverEditLayer)

    if (maneuversEditable) {
      const maneuverTranslate = new Translate({ layers: [maneuverEditLayer] })
      maneuverTranslate.on('translateend', (evt: any) => {
        for (const feature of evt.features.getArray()) {
          const maneuverId = feature.get('maneuver_id')
          const dropped = (feature.getGeometry() as any).getCoordinates()
          const [lon, lat] = toLonLat(dropped)
          const idx = snapToTrack([lon, lat])
          const coords = activities[0]?.geojson?.geometry?.coordinates ?? []
          const times = activities[0]?.time ?? []
          const pt = coords[idx]
          if (!pt) continue
          // Snap the marker to the track point immediately
          ;(feature.getGeometry() as any).setCoordinates(fromLonLat(pt))
          const ts = times[idx] != null ? new Date(times[idx] * 1000).toISOString() : undefined
          emit('reposition-maneuver', { id: maneuverId, latitude: pt[1], longitude: pt[0], timestamp: ts })
        }
      })
      map.addInteraction(maneuverTranslate)
    }

    // "+" editor: transient Draw interaction for whichever add-mode is active
    let activeAddInteraction: any = null
    watch(addMode, (mode) => {
      if (activeAddInteraction) {
        map.removeInteraction(activeAddInteraction)
        activeAddInteraction = null
      }
      if (!mode) return
      const isLine = mode === 'gate_start' || mode === 'gate_start_finish' || mode === 'gate_finish' || mode === 'gate'
      activeAddInteraction = new Draw({
        type: isLine ? 'LineString' : 'Point',
        ...(isLine ? { maxPoints: 2 } : {}),
      })
      activeAddInteraction.on('drawend', (evt: any) => {
        const feature = geoJsonFormat.writeFeatureObject(evt.feature, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:4326',
        })
        if (mode === 'maneuver') {
          const idx = snapToTrack(feature.geometry.coordinates)
          const coords = activities[0]?.geojson?.geometry?.coordinates ?? []
          const times = activities[0]?.time ?? []
          const pt = coords[idx] ?? feature.geometry.coordinates
          const ts = times[idx] != null ? new Date(times[idx] * 1000).toISOString() : new Date().toISOString()
          emit('add-maneuver', { maneuver_type: pendingManeuverType.value, latitude: pt[1], longitude: pt[0], timestamp: ts })
        } else {
          feature.properties = mode === 'gate_start' ? { featureType: 'gate', gateType: 'start' }
            : mode === 'gate_start_finish' ? { featureType: 'gate', gateType: 'start_finish' }
            : mode === 'gate_finish' ? { featureType: 'gate', gateType: 'finish' }
            : mode === 'gate' ? { featureType: 'gate', gateType: 'gate' }
            : { featureType: 'mark', markType: 'mark' }
          emit('update:drawnFeatures', insertCourseFeature(drawnFeatures, feature))
        }
        addMode.value = null
      })
      map.addInteraction(activeAddInteraction)
    })
  } catch (e) {
    console.error(e)
  }
})
</script>

<template>
  <ol-map
      ref="mapRef"
      :loadTilesWhileAnimating="true"
      :loadTilesWhileInteracting="true"
      :style="(fillParent ? 'height: 100%' : `height: ${mHeight}px`) + '; position: relative'"
  >
    <ol-view
      ref="viewRef"
      projection="EPSG:3857"
      :max-zoom="20"
      :zoom="zoom"
      :center="center"
    />

    <!-- Compass button resetting rotation to north-up; auto-hides when not rotated -->
    <ol-rotate-control />

    <div class="map-toggle-stack">
      <v-btn
        v-if="showZoomButtons"
        icon
        size="x-small"
        color="white"
        elevation="2"
        @click="zoomByDelta(1)"
      >
        <v-icon size="16">mdi-plus</v-icon>
        <v-tooltip activator="parent" location="left">Zoom in</v-tooltip>
      </v-btn>
      <v-btn
        v-if="showZoomButtons"
        icon
        size="x-small"
        color="white"
        elevation="2"
        @click="zoomByDelta(-1)"
      >
        <v-icon size="16">mdi-minus</v-icon>
        <v-tooltip activator="parent" location="left">Zoom out</v-tooltip>
      </v-btn>

      <v-btn
        v-if="showViewControls"
        icon
        size="x-small"
        color="white"
        elevation="2"
        @click="resetZoom"
      >
        <v-icon size="16">mdi-image-filter-center-focus</v-icon>
        <v-tooltip activator="parent" location="left">Reset zoom</v-tooltip>
      </v-btn>

      <v-btn
        v-if="showViewControls && followableBoats.length === 1"
        icon
        size="x-small"
        :color="isFollowing ? 'primary' : 'white'"
        elevation="2"
        @click="toggleFollowBoat(followableBoats[0].key)"
      >
        <v-icon size="16">mdi-crosshairs-gps</v-icon>
        <v-tooltip activator="parent" location="left">
          {{ isFollowing ? 'Following boat' : 'Follow boat' }}
        </v-tooltip>
      </v-btn>

      <v-menu v-else-if="showViewControls && followableBoats.length > 1" location="left" :close-on-content-click="false">
        <template #activator="{ props: menuProps }">
          <v-btn
            icon
            size="x-small"
            :color="isFollowing ? 'primary' : 'white'"
            elevation="2"
            v-bind="menuProps"
          >
            <v-icon size="16">mdi-crosshairs-gps</v-icon>
            <v-tooltip activator="parent" location="left">
              {{ isFollowing ? 'Following boat(s)' : 'Follow a boat' }}
            </v-tooltip>
          </v-btn>
        </template>
        <v-card min-width="200">
          <v-list density="compact">
            <v-list-subheader>Follow on map</v-list-subheader>
            <v-list-item
              v-for="entry in followableBoats"
              :key="entry.key"
              @click="toggleFollowBoat(entry.key)"
            >
              <template #prepend>
                <v-icon size="18" :color="followedBoatKeys.has(entry.key) ? 'primary' : 'grey'">
                  {{ followedBoatKeys.has(entry.key) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                </v-icon>
              </template>
              <v-list-item-title class="d-flex align-center ga-2">
                <span class="follow-dot" :style="{ background: entry.color }" />
                {{ entry.label }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <v-btn
        v-if="windDirection != null"
        icon
        size="x-small"
        :color="isWindUp ? 'primary' : 'white'"
        elevation="2"
        @click="toggleWindUp"
      >
        <v-icon size="16">mdi-windsock</v-icon>
        <v-tooltip activator="parent" location="left">
          {{ isWindUp ? 'Back to north up' : 'Rotate map so wind comes from top' }}
        </v-tooltip>
      </v-btn>
      <v-btn
        v-if="hasBoatMetrics"
        icon
        size="x-small"
        :color="allBoatsExpanded ? 'primary' : 'white'"
        elevation="2"
        @click="toggleAllBoats"
      >
        <v-icon size="16">mdi-speedometer</v-icon>
        <v-tooltip activator="parent" location="left">
          {{ allBoatsExpanded ? 'Hide all boat info' : 'Show all boat info' }}
        </v-tooltip>
      </v-btn>
    </div>

    <!-- Inline maneuver/course editor: labelled "+" button to add, chip + cancel while placing
         (also for a placement mode started externally, e.g. from an "Edit course" dialog). -->
    <div
        v-if="maneuversEditable || courseEditable || addMode"
        class="map-add-fab"
        :class="{ 'map-add-fab-highlight': highlightAdd }"
    >
      <v-chip
        v-if="addMode"
        size="small"
        color="primary"
        closable
        close-icon="mdi-close"
        @click:close="cancelAdd"
      >
        {{ addModeLabel }}
      </v-chip>
      <v-btn
        v-else
        color="primary"
        elevation="4"
        rounded="pill"
        prepend-icon="mdi-plus"
        class="text-none"
        @click="showAddDialog = true"
      >
        {{ addButtonLabel }}
      </v-btn>
    </div>

    <!-- "+" dialog: choose what to add, then click the map to place it -->
    <v-dialog v-model="showAddDialog" max-width="380">
      <v-card>
        <v-card-title class="pa-4">{{ addButtonLabel }}</v-card-title>
        <v-card-text class="pa-4 pt-0">
          <template v-if="maneuversEditable">
            <div class="text-caption text-medium-emphasis mb-2">Maneuver</div>
            <div class="d-flex flex-wrap ga-1 mb-4">
              <v-chip
                v-for="t in MANEUVER_TYPES"
                :key="t.value"
                size="small"
                :style="{ background: t.color, color: 'white', cursor: 'pointer' }"
                @click="startAddManeuver(t.value)"
              >
                {{ t.title }}
              </v-chip>
            </div>
          </template>

          <template v-if="courseEditable">
            <div class="text-caption text-medium-emphasis mb-2">Course element</div>
            <div class="d-flex flex-column ga-1">
              <v-btn
                v-for="c in COURSE_ADD_MODES"
                :key="c.mode"
                variant="tonal"
                :prepend-icon="c.icon"
                @click="startAddCourse(c.mode)"
              >
                {{ c.label }}
              </v-btn>
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="showAddDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete-confirm popup for an existing maneuver or course mark -->
    <ol-overlay
        v-if="deleteConfirm"
        :position="deleteConfirm.coord"
        positioning="bottom-center"
        :offset="[0, -16]"
    >
      <v-card class="pa-2 text-caption" elevation="4" style="min-width:160px">
        <div class="font-weight-bold text-capitalize mb-1">
          {{ deleteConfirm.kind === 'maneuver' ? `${deleteConfirm.label} · ${deleteConfirm.typeDisplay}` : deleteConfirm.label }}
        </div>
        <template v-if="deleteConfirm.kind === 'maneuver'">
          <div v-if="deleteConfirm.stats.entry != null">Entry speed: {{ formatSpeed(deleteConfirm.stats.entry) }}</div>
          <div v-if="deleteConfirm.stats.exit != null">Exit speed: {{ formatSpeed(deleteConfirm.stats.exit) }}</div>
          <div v-if="deleteConfirm.stats.tackAngle != null">Tack angle: {{ Math.round(deleteConfirm.stats.tackAngle) }}°</div>
          <div v-if="deleteConfirm.stats.heightChange != null">
            Height change: {{ (deleteConfirm.stats.heightChange > 0 ? '+' : '') + deleteConfirm.stats.heightChange.toFixed(1) }} m
          </div>
        </template>
        <div class="d-flex ga-1 mt-1">
          <v-btn size="x-small" color="error" variant="flat" prepend-icon="mdi-delete" @click="confirmDelete">
            Delete
          </v-btn>
          <v-btn size="x-small" variant="text" @click="deleteConfirm = null">Cancel</v-btn>
        </div>
      </v-card>
    </ol-overlay>

    <ol-tile-layer>
      <ol-source-xyz
          attributions='Powered by <a href="https://www.geoapify.com/" target="_blank">Geoapify</a> | <a href="https://openmaptiles.org/" target="_blank">© OpenMapTiles</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap</a> contributors'
          url="https://maps.geoapify.com/v1/tile/positron/{z}/{x}/{y}.png?apiKey=7e2c8b4970dd4cb78cd5ba2ab27aa916"
      />
    </ol-tile-layer>


    <!-- Highlighted feature -->
    <template  v-if="highlightedFeatures.length">
      <ol-vector-layer
        v-for="feature in highlightedFeatures"
      >
        <ol-source-vector>
          <ol-feature>
            <ol-geom-line-string
                :coordinates="feature.geometry.coordinates.map(c => fromLonLat(c))"
            ></ol-geom-line-string>
            <ol-style>
              <ol-style-stroke
                  color="red"
                  width="2"
              ></ol-style-stroke>
            </ol-style>
          </ol-feature>
        </ol-source-vector>
      </ol-vector-layer>
    </template>

    <!-- Activities: full track, always vivid/full-opacity with stable per-point colour -->
    <ol-vector-layer
      v-for="(feature, i) in geoJsonFeatures"
      :key="i"
    >
      <ol-source-vector>
        <ol-feature
          :properties="feature.properties"
        >
          <ol-geom-line-string
              :coordinates="feature.geometry.coordinates"
          ></ol-geom-line-string>
          <ol-style :override-style-function="() => trackSegmentStyles[i]" />
        </ol-feature>
      </ol-source-vector>
    </ol-vector-layer>

    <!-- Activities: not-yet-sailed portion dimmed on top during playback. A plain
         solid stroke (not FlowLine) so its growing/shrinking geometry never causes
         per-vertex colour resampling, and its cut point stays exactly at the replay
         index instead of snapping to FlowLine's ~255-chunk grid. -->
    <template v-if="isPlaybackActive">
      <template v-for="(feature, i) in unplayedGeoJsonFeatures" :key="`unplayed-${i}`">
        <ol-vector-layer v-if="feature">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-line-string :coordinates="feature.geometry.coordinates"></ol-geom-line-string>
              <ol-style>
                <ol-style-stroke color="rgba(255,255,255,0.65)" :width="3" />
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
      </template>
    </template>

    <!-- Boat Tails (recent track segment, rendered above full tracks) -->
    <template v-if="boatTails.length">
      <ol-vector-layer v-for="(tail, i) in boatTails" :key="`tail-${i}`">
        <ol-source-vector>
          <ol-feature>
            <ol-geom-line-string
                :coordinates="tail.geometry.coordinates.map(c => fromLonLat(c))"
            ></ol-geom-line-string>
            <ol-style>
              <ol-style-stroke
                  :color="tail.properties.color"
                  :width="4"
              ></ol-style-stroke>
            </ol-style>
          </ol-feature>
        </ol-source-vector>
      </ol-vector-layer>
    </template>

    <!-- Boat Positions (rendered on top of tails). Reads displayBoatPositions so a
         hovered track can preview its boat at the hovered point. -->
    <ol-vector-layer
        v-for="(boat, i) in displayBoatPositions"
        :key="`boat-${i}`"
    >
      <ol-source-vector>
        <ol-feature>
          <ol-geom-point
              :coordinates="fromLonLat(boat.geometry.coordinates)"
          ></ol-geom-point>
          <ol-style>
            <ol-style-icon
                src="/boat.svg"
                :scale="1"
                :color="boat.properties.color"
                :rotation="boat.properties.heading_rad"
                :rotate-with-view="true"
            ></ol-style-icon>
            <ol-style-text
                v-if="boat.properties.label"
                :text="boat.properties.label"
                font="bold 12px sans-serif"
                :offset-y="20"
                :fill="boat.properties.color"
            ></ol-style-text>
          </ol-style>
        </ol-feature>
      </ol-source-vector>
    </ol-vector-layer>

    <!-- Boat info card: shown when the boat is clicked (sticky) or while its track
         line / marker is hovered (transient) — the latter makes it easy to tell
         which track belongs to which boat. -->
    <template v-for="{ boat, key } in boatsWithMetrics" :key="`boat-metrics-${key}`">
      <ol-overlay
          v-if="expandedBoatKeys.has(key) || hoveredTrack?.key === key"
          :position="fromLonLat(boat.geometry.coordinates)"
          positioning="center-left"
          :offset="boatCardOffset(key)"
      >
        <v-card
            class="pa-2 pr-7 text-caption boat-metrics-card"
            elevation="4"
            :style="{ borderLeft: `3px solid ${boat.properties.color}` }"
            @pointerdown="startBoatCardDrag(key, $event)"
        >
          <v-btn
              icon
              size="x-small"
              variant="text"
              density="compact"
              class="boat-metrics-close"
              @click="collapseBoat(key)"
          >
            <v-icon size="x-small">mdi-close</v-icon>
          </v-btn>
          <div v-if="boat.properties.label" class="font-weight-bold text-truncate" style="max-width:140px">
            {{ boat.properties.label }}
          </div>
          <div
              v-if="boat.properties.skipper && boat.properties.skipper !== boat.properties.label"
              class="text-medium-emphasis text-truncate"
              style="max-width:140px"
          >
            {{ boat.properties.skipper }}
          </div>
          <div v-if="boat.properties.label" class="mb-1" />
          <div v-if="boat.properties.metrics.time" class="d-flex align-center ga-1">
            <v-icon size="12">mdi-clock-outline</v-icon>
            <span>{{ boat.properties.metrics.time }}</span>
          </div>
          <div v-if="boat.properties.metrics.speed" class="d-flex align-center ga-1">
            <v-icon size="12">mdi-speedometer</v-icon>
            <span>{{ boat.properties.metrics.speed }}</span>
          </div>
          <div v-if="boat.properties.metrics.heading" class="d-flex align-center ga-1">
            <v-icon size="12">mdi-compass</v-icon>
            <span>{{ boat.properties.metrics.heading }}</span>
          </div>
        </v-card>
      </ol-overlay>
    </template>

    <!-- Hover hint: "Show/Hide boat info" -->
    <ol-overlay
        v-if="hoveredBoatHint"
        :position="hoveredBoatHint.coord"
        positioning="bottom-center"
        :offset="[0, -16]"
    >
      <div class="boat-hover-hint">{{ hoveredBoatHint.text }}</div>
    </ol-overlay>

    <!-- Course -->
    <template v-if="course">
      <template v-for="mark in course.marks">
        <ol-vector-layer v-if="mark.type === 'SF'">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-line-string
                  :coordinates="[[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]].map(c => fromLonLat(c))"
              ></ol-geom-line-string>
              <ol-style>
                <ol-style-stroke
                    color="red"
                    width="2"
                ></ol-style-stroke>
                <ol-style-text text="Start / Finish" fill="red"></ol-style-text>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
        <ol-vector-layer v-if="mark.type === 'ST'">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-line-string
                  :coordinates="[[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]].map(c => fromLonLat(c))"
              ></ol-geom-line-string>
              <ol-style>
                <ol-style-stroke
                    color="red"
                    width="2"
                ></ol-style-stroke>
                <ol-style-text text="Start" fill="red"></ol-style-text>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
        <ol-vector-layer v-if="mark.type == 'MA'">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-point
                  :coordinates="fromLonLat([mark.longitude1, mark.latitude1])"
              ></ol-geom-point>
              <ol-style>
                <ol-style-circle :radius="3">
                  <ol-style-fill color="red"></ol-style-fill>
                </ol-style-circle>
                <ol-style-text text="Mark" fill="red" :offset-x="20"></ol-style-text>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
        <ol-vector-layer v-if="mark.type === 'FN'">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-line-string
                  :coordinates="[[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]].map(c => fromLonLat(c))"
              ></ol-geom-line-string>
              <ol-style>
                <ol-style-stroke color="#f44336" width="2"></ol-style-stroke>
                <ol-style-text text="Finish" fill="#f44336"></ol-style-text>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
        <ol-vector-layer v-if="mark.type == 'SE'">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-point
                  :coordinates="fromLonLat([mark.longitude1, mark.latitude1])"
              ></ol-geom-point>
              <ol-style>
                <ol-style-circle :radius="3">
                  <ol-style-fill color="red"></ol-style-fill>
                </ol-style-circle>
                <ol-style-text text="Sector" fill="red" :offset-x="20"></ol-style-text>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
        <ol-vector-layer v-if="mark.type === 'FI'">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-line-string
                  :coordinates="[[mark.longitude1, mark.latitude1], [mark.longitude2, mark.latitude2]].map(c => fromLonLat(c))"
              ></ol-geom-line-string>
              <ol-style>
                <ol-style-stroke
                    color="red"
                    width="2"
                ></ol-style-stroke>
                <ol-style-text text="Finish" fill="red"></ol-style-text>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
      </template>


    </template>

    <!-- League Race marks -->
    <template  v-if="leagueRaceResult">

      <ol-vector-layer>
        <ol-source-vector>
          <ol-feature>
            <ol-geom-line-string
                :coordinates="getStartLineFeature(leagueRaceResult.race_details).geometry.coordinates.map(c => fromLonLat(c))"
            ></ol-geom-line-string>
            <ol-style>
              <ol-style-stroke
                  color="red"
                  width="2"
              ></ol-style-stroke>
              <ol-style-text text="Start / Finish" fill="red"></ol-style-text>
            </ol-style>
          </ol-feature>
        </ol-source-vector>
      </ol-vector-layer>
      <ol-vector-layer>
        <ol-source-vector>
          <ol-feature>
            <ol-geom-polygon
                :coordinates="getRaceMarkFeature(leagueRaceResult.race_details).geometry.coordinates.map(e => e.map(c => fromLonLat(c)))"
            ></ol-geom-polygon>
            <ol-style>
              <ol-style-stroke
                  color="red"
                  width="2"
              ></ol-style-stroke>
              <ol-style-text text="Windward Mark Sector" fill="red"></ol-style-text>
            </ol-style>
          </ol-feature>
        </ol-source-vector>
      </ol-vector-layer>
    </template>

    <!-- Active draw interaction (placed at map level, not inside a layer source) -->
    <ol-interaction-draw
      v-if="drawingType"
      :type="drawingType"
      :features="drawCollection"
      :stop-click="true"
      :max-points="drawMaxPoints"
      @drawend="onDrawEnd"
    />

    <!-- Analysis tack waypoints (shown only when no Maneuver records exist) -->
    <template v-if="tacks?.length">
      <ol-vector-layer v-for="(tack, i) in tacks" :key="`tack-${i}`">
        <ol-source-vector>
          <ol-feature>
            <ol-geom-point :coordinates="fromLonLat([tack.lng, tack.lat])"></ol-geom-point>
            <ol-style>
              <ol-style-circle :radius="6">
                <ol-style-fill color="rgba(255, 165, 0, 0.8)"></ol-style-fill>
                <ol-style-stroke color="white" :width="1.5"></ol-style-stroke>
              </ol-style-circle>
              <ol-style-text
                  :text="`T${i + 1}`"
                  :offset-y="-14"
              ></ol-style-text>
            </ol-style>
          </ol-feature>
        </ol-source-vector>
      </ol-vector-layer>
    </template>

    <!-- Maneuver markers (tacks, gybes, mark roundings) are rendered by the
         imperative maneuverEditLayer (added in onMounted) so they support
         drag-to-reposition and click-to-pick; see maneuverEditSource sync above. -->

    <!-- Maneuver hover details: quick glance only (non-interactive by design —
         see click/tap delete-confirm popup above for the interactive version).
         Suppressed while that popup is open so the two don't overlap. -->
    <ol-overlay
        v-if="hoveredManeuver && hoveredManeuverDetails && !deleteConfirm"
        :position="hoveredManeuver.coord"
        positioning="bottom-center"
        :offset="[0, -16]"
    >
      <v-card class="pa-2 text-caption" elevation="4" style="pointer-events: none; min-width: 160px">
        <div class="font-weight-bold">
          {{ hoveredManeuver.label }} · {{ hoveredManeuver.m.maneuver_type_display }}
        </div>
        <div v-if="hoveredManeuverDetails.entry != null">
          Entry speed: {{ formatSpeed(hoveredManeuverDetails.entry) }}
        </div>
        <div v-if="hoveredManeuverDetails.exit != null">
          Exit speed: {{ formatSpeed(hoveredManeuverDetails.exit) }}
        </div>
        <div v-if="hoveredManeuverDetails.tackAngle != null">
          Tack angle: {{ Math.round(hoveredManeuverDetails.tackAngle) }}°
        </div>
        <div v-if="hoveredManeuverDetails.heightChange != null">
          Height change: {{ (hoveredManeuverDetails.heightChange > 0 ? '+' : '') + hoveredManeuverDetails.heightChange.toFixed(1) }} m
        </div>
      </v-card>
    </ol-overlay>

  </ol-map>

</template>

<style scoped>
/* Hide OpenLayers' default top-left zoom (+ / −) control — we render our own
   zoom-in/zoom-out buttons in .map-toggle-stack so they match the other map
   buttons' style (white, elevated, top-right). */
:deep(.ol-zoom) {
  display: none;
}

/* Below the OL rotate control (top-right, .5em), above the map viewport */
.map-toggle-stack {
  position: absolute;
  top: 2.75em;
  right: 0.5em;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4em;
}
.follow-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.boat-metrics-card {
  position: relative;
  min-width: 130px;
  cursor: grab;
  touch-action: none;
  user-select: none;
}
.boat-metrics-card:active {
  cursor: grabbing;
}
.boat-metrics-close {
  position: absolute;
  top: 2px;
  right: 2px;
}
.boat-hover-hint {
  pointer-events: none;
  background: rgba(33, 33, 33, 0.85);
  color: white;
  font-size: 11px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}
.map-add-fab {
  /* Bottom-left: bottom-right is occupied by OL's default attribution control */
  position: absolute;
  bottom: 0.75em;
  left: 0.5em;
  z-index: 1;
  display: flex;
  align-items: center;
}
/* A separate ring on the wrapper, not the button itself -- Vuetify's elevation="4" already sets
   the button's own box-shadow with !important, which silently wins over (i.e. hides) an animation
   targeting box-shadow on the button. An independent ::after pseudo-element sidesteps that entirely. */
.map-add-fab-highlight::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 9999px;
  border: 3px solid rgb(13, 71, 161);
  pointer-events: none;
  animation: map-add-fab-ping 0.8s ease-out 2;
}
/* The button is a wide labelled pill now, so the ring grows less than it did around the round "+". */
@keyframes map-add-fab-ping {
  0% { transform: scale(0.95); opacity: 0.9; }
  100% { transform: scale(1.2); opacity: 0; }
}
</style>
